import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Divider,
  Slider
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BaseModal from './BaseModal';
import { BACKGROUND_COLORS } from '../transcription/constants';
import { useColorPickerModal } from '../../context/ColorPickerContext';
import { getSentence } from '../translations';


// Memoized color conversion utilities - moved outside component to prevent recreation
const colorUtils = {
  // HSV to RGB conversion
  hsvToRgb: (h, s, v) => {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    
    let r, g, b;
    if (h >= 0 && h < 60) {
      r = c; g = x; b = 0;
    } else if (h >= 60 && h < 120) {
      r = x; g = c; b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0; g = c; b = x;
    } else if (h >= 180 && h < 240) {
      r = 0; g = x; b = c;
    } else if (h >= 240 && h < 300) {
      r = x; g = 0; b = c;
    } else {
      r = c; g = 0; b = x;
    }
    
    return [
      Math.round((r + m) * 255),
      Math.round((g + m) * 255),
      Math.round((b + m) * 255)
    ];
  },

  // RGB to HSV conversion
  rgbToHsv: (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const diff = max - min;
    
    let h = 0;
    if (diff !== 0) {
      if (max === r) {
        h = ((g - b) / diff) % 6;
      } else if (max === g) {
        h = (b - r) / diff + 2;
      } else {
        h = (r - g) / diff + 4;
      }
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;
    
    const s = max === 0 ? 0 : diff / max;
    const v = max;
    
    return [h, Math.round(s * 100), Math.round(v * 100)];
  },

  // RGB to Hex conversion
  rgbToHex: (r, g, b) => {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  },

  // Hex to RGB conversion
  hexToRgb: (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  }
};

const GlobalColorPickerModal = React.memo(() => {
  const {
    isOpen,
    speaker,
    currentColor,
    meetingId,
    isLoading,
    closeColorPicker,
    updateSpeakerColor
  } = useColorPickerModal();

  // Local state for modal interactions
  const [selectedColor, setSelectedColor] = useState('');
  const [customColor, setCustomColor] = useState('');
  const [hsv, setHsv] = useState([0, 0, 100]);
  const [isHsvExpanded, setIsHsvExpanded] = useState(false);

  // Initialize HSV from current color - memoized to prevent recalculation
  const initializeHSV = useCallback((color) => {
    const [r, g, b] = colorUtils.hexToRgb(color || '#a6cee3');
    return colorUtils.rgbToHsv(r, g, b);
  }, []);

  // Memoize HSV initialization to prevent recalculation
  const initializedHSV = useMemo(() => {
    return currentColor ? initializeHSV(currentColor) : [0, 0, 100];
  }, [currentColor, initializeHSV]);

  // Reset state when modal opens with new color - optimized
  useEffect(() => {
    if (isOpen && currentColor) {
      setSelectedColor(currentColor);
      setCustomColor('');
      setHsv(initializedHSV); // Use pre-calculated HSV
      setIsHsvExpanded(false);
    }
  }, [isOpen, currentColor, initializedHSV]);

  // Memoized handlers to prevent unnecessary rerenders
  const handleColorSelect = useCallback((color) => {
    setSelectedColor(color);
    setCustomColor('');
    const [r, g, b] = colorUtils.hexToRgb(color);
    setHsv(colorUtils.rgbToHsv(r, g, b));
  }, []);

  const handleCustomColorChange = useCallback((event) => {
    const color = event.target.value;
    setCustomColor(color);
    setSelectedColor(color);
    const [r, g, b] = colorUtils.hexToRgb(color);
    setHsv(colorUtils.rgbToHsv(r, g, b));
  }, []);

  const handleHsvChange = useCallback((component, value) => {
    setHsv(currentHsv => {
      const newHsv = [...currentHsv];
      newHsv[component] = value;
      
      const [r, g, b] = colorUtils.hsvToRgb(newHsv[0], newHsv[1] / 100, newHsv[2] / 100);
      const hexColor = colorUtils.rgbToHex(r, g, b);
      setSelectedColor(hexColor);
      setCustomColor('');
      
      return newHsv;
    });
  }, []);

  const handleSaveColor = useCallback(async () => {
    if (!selectedColor || !speaker || !meetingId) return;
    await updateSpeakerColor(speaker, selectedColor, meetingId);
  }, [selectedColor, speaker, meetingId, updateSpeakerColor]);

  const handleToggleHsv = useCallback(() => {
    setIsHsvExpanded(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    // Reset local state
    setSelectedColor('');
    setCustomColor('');
    setHsv([0, 0, 100]);
    setIsHsvExpanded(false);
    closeColorPicker();
  }, [closeColorPicker]);

  // Extract HSV values for cleaner dependency arrays
  const hue = hsv[0];
  const saturation = hsv[1];
  const brightness = hsv[2];

  // Memoize expensive gradient calculations for sliders
  const saturationGradient = useMemo(() => {
    const leftColor = colorUtils.rgbToHex(...colorUtils.hsvToRgb(hue, 0, brightness / 100));
    const rightColor = colorUtils.rgbToHex(...colorUtils.hsvToRgb(hue, 1, brightness / 100));
    return `linear-gradient(to right, ${leftColor}, ${rightColor})`;
  }, [hue, brightness]);

  const brightnessGradient = useMemo(() => {
    const rightColor = colorUtils.rgbToHex(...colorUtils.hsvToRgb(hue, saturation / 100, 1));
    return `linear-gradient(to right, #000000, ${rightColor})`;
  }, [hue, saturation]);

  // Memoize actions to prevent recreation
  const actions = useMemo(() => (
    <>
      <Button 
        variant="outlined" 
        onClick={handleClose}
        disabled={isLoading}
        sx={{ 
          textTransform: 'none',
          minWidth: { xs: 'auto', sm: '100px' }
        }}
      >
        {getSentence(8)}
      </Button>
      <Button 
        variant="contained" 
        onClick={handleSaveColor}
        disabled={!selectedColor || isLoading}
        sx={{ 
          textTransform: 'none',
          minWidth: { xs: 'auto', sm: '100px' }
        }}
      >
        {isLoading ? 'Saving...' : 'Save Color'}
      </Button>
    </>
  ), [handleClose, handleSaveColor, isLoading, selectedColor]);

  // Don't render if modal is not open
  if (!isOpen) return null;

  return (
    <BaseModal
      open={isOpen}
      onClose={handleClose}
      title={`Choose Color for ${speaker}`}
      size="small"
      maxWidth="xs"
      actions={actions}
      PaperProps={{
        sx: {
          maxWidth: '350px',
        }
      }}
    >
      <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>

        {/* Predefined Colors */}
        <Typography variant="body2" color="text.secondary" mb={1}>
          Choose from palette:
        </Typography>
        <Grid container spacing={1} mb={2}>
          {BACKGROUND_COLORS.map((color, index) => (
            <Grid item key={index}>
              <Box
                onClick={() => !isLoading && handleColorSelect(color)}
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  backgroundColor: color,
                  border: selectedColor === color ? '3px solid #1976d2' : '2px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  '&:hover': !isLoading ? {
                    transform: 'scale(1.1)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                  } : {}
                }}
              >
                {selectedColor === color && (
                  <CheckIcon
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: 'white',
                      fontSize: { xs: 18, sm: 20 },
                      filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))'
                    }}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* HSV Color Picker */}
        <Box
          onClick={() => !isLoading && handleToggleHsv()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            padding: { xs: '8px 12px', sm: '4px 0px' },
            marginBottom: isHsvExpanded ? 1 : 2,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            '&:hover': !isLoading ? {
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.12)'
            } : {},
            borderRadius: 1,
            transition: 'all 0.2s ease'
          }}
        >
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Or customize with HSV sliders:
          </Typography>
          {isHsvExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>

        {/* Collapsible HSV Content */}
        {isHsvExpanded && (
          <Box>
            {/* Hue Slider */}
            <Box mb={2}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                mb={0.5}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Hue: {hsv[0]}°
              </Typography>
              <Slider
                value={hsv[0]}
                onChange={(_, value) => !isLoading && handleHsvChange(0, value)}
                min={0}
                max={360}
                disabled={isLoading}
                sx={{
                  '& .MuiSlider-track': {
                    background: 'transparent',
                    height: { xs: 6, sm: 8 },
                    border: 'none'
                  },
                  '& .MuiSlider-rail': {
                    background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
                    height: { xs: 6, sm: 8 },
                    opacity: 1
                  }
                }}
              />
            </Box>

            {/* Saturation Slider */}
            <Box mb={2}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                mb={0.5}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Saturation: {hsv[1]}%
              </Typography>
              <Slider
                value={hsv[1]}
                onChange={(_, value) => !isLoading && handleHsvChange(1, value)}
                min={0}
                max={100}
                disabled={isLoading}
                sx={{
                  '& .MuiSlider-track': {
                    background: 'transparent',
                    height: { xs: 6, sm: 8 },
                    border: 'none'
                  },
                  '& .MuiSlider-rail': {
                    background: saturationGradient,
                    height: { xs: 6, sm: 8 },
                    opacity: 1
                  }
                }}
              />
            </Box>

            {/* Value/Brightness Slider */}
            <Box mb={2}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                mb={0.5}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Brightness: {hsv[2]}%
              </Typography>
              <Slider
                value={hsv[2]}
                onChange={(_, value) => !isLoading && handleHsvChange(2, value)}
                min={0}
                max={100}
                disabled={isLoading}
                sx={{
                  '& .MuiSlider-track': {
                    background: 'transparent',
                    height: { xs: 6, sm: 8 },
                    border: 'none'
                  },
                  '& .MuiSlider-rail': {
                    background: brightnessGradient,
                    height: { xs: 6, sm: 8 },
                    opacity: 1
                  }
                }}
              />
            </Box>

            {/* Color Preview with Hex Input */}
            <Box mb={2}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                mb={0.5}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Preview:
              </Typography>
              <Box
                sx={{
                  height: { xs: 35, sm: 40 },
                  backgroundColor: selectedColor,
                  border: '2px solid #e0e0e0',
                  borderRadius: 1,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  padding: { xs: 0.3, sm: 0.5 }
                }}
              >
                <TextField
                  value={customColor || selectedColor}
                  onChange={handleCustomColorChange}
                  size="small"
                  placeholder="#FF5733"
                  disabled={isLoading}
                  sx={{
                    width: { xs: '60%', sm: '50%' },
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    '& .MuiInputBase-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      fontSize: { xs: '10px', sm: '11px' },
                      height: { xs: '24px', sm: '28px' }
                    },
                    '& .MuiInputBase-input': {
                      padding: { xs: '2px 4px', sm: '4px 6px' },
                      textAlign: 'center',
                      fontFamily: 'monospace'
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </BaseModal>
  );
});

// Add display name for better debugging
GlobalColorPickerModal.displayName = 'GlobalColorPickerModal';

export default GlobalColorPickerModal;
